import { Injectable, inject, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, catchError, switchMap, map, of, tap } from 'rxjs';
import { XrSession } from '../models/session.model';
import { GenericApiService } from './generic-api.service';

@Injectable({
  providedIn: 'root'
})
export class TelemetryService {
  private api = inject(GenericApiService);
  private http = inject(HttpClient); // Solamente necesario para subida directa a GCS (Signed URL)

  // Filter state for Sessions Explorer
  sessionsSearchQuery = signal<string>('');
  sessionsTimeRange = signal<string>('7d');
  sessionsCustomStartDate = signal<string>('');
  sessionsCustomEndDate = signal<string>('');
  sessionsSelectedApp = signal<string>('all');
  
  private lastSessionsCache: { params: string, data: any } | null = null;

  getApps(): Observable<string[]> {
    return this.api.query<string[]>('LIST_APPS').pipe(
      catchError(err => {
        console.error('Error in TelemetryService.getApps', err);
        throw err;
      })
    );
  }

  getSessions(page?: number, limit?: number, appName?: string, startDate?: string, endDate?: string, search?: string, contextId?: string): Observable<any> {
    const payload = { page, limit, appName, startDate, endDate, search, contextId };
    const paramsString = JSON.stringify(payload);
    
    if (this.lastSessionsCache && this.lastSessionsCache.params === paramsString) {
      return of(this.lastSessionsCache.data);
    }

    return this.api.query<any>('LIST_SESSIONS', payload).pipe(
      tap(data => {
        this.lastSessionsCache = { params: paramsString, data };
      }),
      catchError(err => {
        console.error('Error in TelemetryService.getSessions', err);
        throw err;
      })
    );
  }

  getSession(id: string): Observable<XrSession> {
    return this.api.query<XrSession>('GET_SESSION', { id });
  }

  getSessionTelemetry(id: string, page: number = 1, limit: number = 10): Observable<any[]> {
    return this.api.query<any[]>('GET_SESSION_TELEMETRY', { id, page, limit });
  }

  getSessionBreadcrumbs(id: string, page: number = 1, limit: number = 10): Observable<any[]> {
    return this.api.query<any[]>('GET_SESSION_BREADCRUMBS', { id, page, limit });
  }

  getSessionSceneSnapshots(id: string, page: number = 1, limit: number = 10): Observable<any[]> {
    return this.api.query<any[]>('GET_SESSION_SCENE_SNAPSHOTS', { id, page, limit });
  }

  getSessionPerformance(id: string, page: number = 1, limit: number = 10): Observable<any[]> {
    return this.api.query<any[]>('GET_SESSION_PERFORMANCE', { id, page, limit });
  }

  getSessionNetworkRequests(id: string, page: number = 1, limit: number = 10): Observable<any[]> {
    return this.api.query<any[]>('GET_SESSION_NETWORK_REQUESTS', { id, page, limit });
  }


  updateModelTransform(sessionId: string, objectName: string, rotationOffset: any, positionOffset: any): Observable<any> {
    const payload = {
      action: 'UPDATE_OBJECT_TRANSFORM',
      id: sessionId,
      objectName,
      rotationOffset,
      positionOffset
    };
    return this.api.query<any>('UPDATE_OBJECT_TRANSFORM', payload);
  }

  uploadModel(sessionId: string, objectName: string, file: File): Observable<{ url: string }> {
    const payload = {
      id: sessionId,
      objectName,
      fileName: file.name,
      contentType: file.type || 'application/octet-stream'
    };
    
    return this.api.query<{ uploadUrl: string, publicUrl: string }>('GENERATE_UPLOAD_URL', payload).pipe(
      switchMap(response => {
        // Upload file directly to Firebase Storage using the Signed URL
        // We use PUT and set the correct content type. No Authorization headers should be sent to GCS.
        return this.http.put(response.uploadUrl, file, { 
          headers: { 'Content-Type': file.type || 'application/octet-stream' } 
        }).pipe(
          map(() => ({ url: response.publicUrl }))
        );
      })
    );
  }

  updateObjectModel(sessionId: string, objectName: string, modelUrl: string): Observable<any> {
    return this.api.query<any>('UPDATE_OBJECT_MODEL', { id: sessionId, objectName, modelUrl });
  }
}
